import React from "react";
import { Sparkles, AlertTriangle, ShieldCheck, RefreshCw, Bot } from "lucide-react";

const fmt = (n) => {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
};

const REC_TONE = {
  approve: { bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.40)", text: "#34d399", label: "Approve" },
  decline: { bg: "rgba(244,63,94,0.10)",  border: "rgba(244,63,94,0.40)",  text: "#fb7185", label: "Decline" },
  review:  { bg: "rgba(234,179,8,0.10)",  border: "rgba(234,179,8,0.40)",  text: "#facc15", label: "Review" },
};

const AIAnalysisCard = ({ analysis, onAnalyze, analyzing, hasDocs }) => {
  if (analysis?.error) {
    return (
      <div className="border border-rose-500/30 bg-rose-500/10 rounded-lg p-5">
        <div className="text-rose-300 text-sm font-semibold mb-1">AI analysis failed</div>
        <div className="text-cf-muted text-xs">{analysis.error}</div>
        {analysis.raw_preview && (
          <pre className="text-[0.7rem] text-cf-muted mt-3 overflow-auto max-h-32 bg-black/30 p-2 rounded">{analysis.raw_preview}</pre>
        )}
        <button onClick={onAnalyze} disabled={analyzing} className="cf-btn-ghost mt-3" style={{ fontSize: "0.7rem" }}>
          <RefreshCw size={12} /> Try again
        </button>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="border border-white/5 rounded-lg p-5" style={{ background: "rgba(10,14,23,0.4)" }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[0.65rem] uppercase tracking-[0.22em] text-cf-gold font-semibold flex items-center gap-1">
              <Bot size={11} /> AI Statement Analysis
            </div>
            <p className="text-cf-muted text-xs mt-1">
              {hasDocs ? "Run AI on uploaded bank statements." : "No documents uploaded yet."}
            </p>
          </div>
          <button onClick={onAnalyze} disabled={!hasDocs || analyzing} className="cf-btn-ghost" style={{ fontSize: "0.7rem" }}>
            <Sparkles size={12} /> {analyzing ? "Analyzing…" : "Analyze with AI"}
          </button>
        </div>
      </div>
    );
  }

  const tone = REC_TONE[analysis.recommendation] || REC_TONE.review;

  return (
    <div data-testid="ai-analysis" className="border rounded-lg p-5" style={{ background: tone.bg, borderColor: tone.border }}>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-[0.65rem] uppercase tracking-[0.22em] font-semibold flex items-center gap-1" style={{ color: tone.text }}>
            <Bot size={11} /> AI Underwriting Memo
          </div>
          <div className="font-serif text-2xl mt-1" style={{ color: tone.text }}>
            Recommend: {tone.label}
          </div>
        </div>
        <button onClick={onAnalyze} disabled={analyzing} className="cf-btn-ghost" style={{ fontSize: "0.65rem", padding: "0.35rem 0.7rem" }}>
          <RefreshCw size={11} /> {analyzing ? "..." : "Re-run"}
        </button>
      </div>

      {analysis.summary_paragraph && (
        <p className="text-sm text-cf-text leading-relaxed mb-4">{analysis.summary_paragraph}</p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
        <Stat label="Monthly deposits" value={analysis.monthly_deposits_label || (analysis.monthly_deposits_avg ? fmt(analysis.monthly_deposits_avg) : "—")} />
        <Stat label="Avg daily balance" value={analysis.avg_daily_balance_label || (analysis.avg_daily_balance ? fmt(analysis.avg_daily_balance) : "—")} />
        <Stat label="NSFs" value={analysis.nsf_count ?? "—"} />
        <Stat label="Negative days" value={analysis.negative_days_count ?? "—"} />
        <Stat label="Balance trend" value={analysis.balance_trend || "—"} />
        <Stat label="Months analyzed" value={analysis.months_analyzed ?? "—"} />
      </div>

      {(analysis.recommended_amount_usd != null || analysis.suggested_factor_rate || analysis.suggested_term_months) && (
        <div className="mt-5 pt-4 border-t border-white/5">
          <div className="text-[0.6rem] uppercase tracking-[0.2em] text-cf-muted mb-2">Suggested terms</div>
          <div className="grid grid-cols-3 gap-x-4 text-sm">
            <Stat label="Amount" value={analysis.recommended_amount_usd ? fmt(analysis.recommended_amount_usd) : "—"} />
            <Stat label="Factor" value={analysis.suggested_factor_rate ? `${analysis.suggested_factor_rate}×` : "—"} />
            <Stat label="Term" value={analysis.suggested_term_months ? `${analysis.suggested_term_months} mo` : "—"} />
          </div>
        </div>
      )}

      {(analysis.risk_flags?.length > 0 || analysis.strengths?.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-4 mt-5 pt-4 border-t border-white/5">
          {analysis.risk_flags?.length > 0 && (
            <div>
              <div className="text-[0.6rem] uppercase tracking-[0.2em] text-cf-muted mb-2 flex items-center gap-1">
                <AlertTriangle size={10} /> Flags
              </div>
              <ul className="space-y-1.5">
                {analysis.risk_flags.map((f, i) => (
                  <li key={i} className="text-xs text-cf-text flex items-start gap-2">
                    <span style={{ background: "#fb7185", width: 4, height: 4, borderRadius: 2, marginTop: 6, flexShrink: 0 }} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {analysis.strengths?.length > 0 && (
            <div>
              <div className="text-[0.6rem] uppercase tracking-[0.2em] text-cf-muted mb-2 flex items-center gap-1">
                <ShieldCheck size={10} /> Strengths
              </div>
              <ul className="space-y-1.5">
                {analysis.strengths.map((s, i) => (
                  <li key={i} className="text-xs text-cf-text flex items-start gap-2">
                    <span style={{ background: "#34d399", width: 4, height: 4, borderRadius: 2, marginTop: 6, flexShrink: 0 }} />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {analysis.top_deposit_sources?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <div className="text-[0.6rem] uppercase tracking-[0.2em] text-cf-muted mb-2">Top deposit sources</div>
          <div className="flex flex-wrap gap-2">
            {analysis.top_deposit_sources.map((s, i) => (
              <span key={i} className="text-xs bg-white/5 text-cf-text px-2 py-1 rounded">{s}</span>
            ))}
          </div>
        </div>
      )}

      <div className="text-[0.6rem] uppercase tracking-[0.18em] text-cf-muted mt-4">
        Model: {analysis.model || "gemini"} · {analysis.analyzed_at && new Date(analysis.analyzed_at).toLocaleString()}
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[0.6rem] uppercase tracking-[0.2em] text-cf-muted mb-1">{label}</div>
    <div className="text-cf-text font-semibold text-sm">{value}</div>
  </div>
);

export default AIAnalysisCard;
